/**
 * Prehung kit options.
 *
 * Companion to snippets/prehung-kit-options.liquid on the product.prehung-kit
 * template. Three jobs:
 *
 *   1. Hinge finish. Door Size and Door Swing are Shopify variant options, so
 *      finish rides on the cart line as a "Hinge Finish" property (same
 *      convention as the door builder). Both finishes cost the same, so
 *      nothing else changes.
 *
 *   2. Keep the photo honest. The gallery holds ONE door image (plus the detail
 *      shots). When the swing or finish changes we swap that single image's
 *      source to the render for the chosen configuration, so only the door the
 *      customer configured is ever shown. Swing/finish -> image URL comes from
 *      the product metafield custom.prehung_config_map, rendered into
 *      [data-prehung-config-map]. Because we only change an <img> src (never
 *      add/remove gallery cells), the theme's Flickity slider, its thumbnail
 *      nav and the lightbox all keep working untouched.
 *
 *      2026-10-20: the photo follows the swing the moment it is clicked and
 *      assumes BLACK hinges until the customer clicks Satin Nickel (the Black
 *      chip itself stays unselected; a hinge colour is still required before
 *      add to cart). A customer-made photo change also brings the gallery back
 *      to the door render, so the change is actually seen.
 *
 *   3. No option gets picked for the customer. When one option is clicked the
 *      theme asks Shopify for the first variant that matches it and re-renders
 *      every option from that variant, which silently checks a swing after a
 *      size click (or a size after a swing click). We remember which options
 *      the customer actually clicked and, after each theme re-render, uncheck
 *      anything they did not choose and clear the variant id, so add to cart
 *      stays gated until size, swing AND hinge colour are all real choices.
 */
(function () {
  "use strict";

  var SWING_OPTION = "door swing";
  var SIZE_OPTION = "door size";
  // The photo assumes black hinges until Satin Nickel is clicked.
  var DEFAULT_FINISH = "Black";

  // A variant change rebuilds the option markup, throwing this container away and
  // building a fresh one. Remember the chosen finish so the new instance restores it.
  var finishMemory = {};
  // Options the customer actually clicked, per form: { "Door Size": "30 x 80", ... }.
  // Anything the theme checks on its own (first available variant) is not in here.
  var chosenMemory = {};

  function memoryKey(container) {
    return container.getAttribute("data-product-form-id") || "kit";
  }

  // "options[Door Swing]" -> "Door Swing"
  function optionName(input) {
    var m = /^options\[(.+)\]$/.exec(input.getAttribute("name") || "");
    return m ? m[1] : "";
  }

  // The theme swaps <variant-selects> by inserting the new one and hiding the old
  // one for ~500 ms before removing it. Only the visible one counts.
  function isLiveInput(input) {
    var selects = input.closest("variant-selects");
    return !selects || selects.style.display !== "none";
  }

  function PrehungKit(container) {
    this.container = container;
    this.productInfo = container.closest("product-info");
    this.formId = container.getAttribute("data-product-form-id") || "";
    this.form = document.getElementById(this.formId);
    if (!this.form && this.productInfo) {
      this.form = this.productInfo.querySelector("form[action*='/cart/add']");
    }

    this.finishInput = container.querySelector("[data-hinge-finish-input]");
    this.errorEl = container.querySelector("[data-prehung-error]");
    this.configMap = this.readJson("[data-prehung-config-map]") || {};
    // No default finish: the page opens with every option unselected (like the
    // door builder), so the customer makes a conscious choice.
    this.finish = "";
    this.doorMediaId = container.getAttribute("data-door-media-id") || "";
    // The image the gallery shows before anything is chosen (its featured/first
    // media), so we can restore it on reset.
    this.defaultImg = this.captureDefaultImage();

    var remembered = finishMemory[memoryKey(container)] || "";
    var rememberedChoices = chosenMemory[memoryKey(container)] || null;

    this.reset();
    this.bindEvents();
    this.installPreRender();

    // Restore the customer's choices after a re-render (chips, hidden input, photo).
    if (rememberedChoices) chosenMemory[memoryKey(container)] = rememberedChoices;
    if (remembered) this.setFinish(remembered);
    this.afterThemeRender();
  }

  PrehungKit.prototype.captureDefaultImage = function () {
    if (!this.doorMediaId) return null;
    var scope = this.getScope();
    var img = scope.querySelector(
      '[data-product-slide][data-id="' + this.doorMediaId + '"] img'
    );
    if (!img) return null;
    return { src: img.getAttribute("src"), srcset: img.getAttribute("srcset") };
  };

  PrehungKit.prototype.readJson = function (selector) {
    var node = this.container.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  PrehungKit.prototype.getScope = function () {
    return this.productInfo || document;
  };

  // The options the customer has clicked so far (shared across re-inits).
  PrehungKit.prototype.chosen = function () {
    var key = memoryKey(this.container);
    if (!chosenMemory[key]) chosenMemory[key] = {};
    return chosenMemory[key];
  };

  PrehungKit.prototype.chosenValue = function (optionKey) {
    var chosen = this.chosen();
    for (var name in chosen) {
      if (chosen.hasOwnProperty(name) && name.toLowerCase() === optionKey) return chosen[name];
    }
    return "";
  };

  // Return every option to unselected and restore the default gallery image.
  // Used on load and after a successful add to cart.
  PrehungKit.prototype.reset = function () {
    this.finish = "";
    delete finishMemory[memoryKey(this.container)];
    delete chosenMemory[memoryKey(this.container)];
    if (this.finishInput) this.finishInput.value = "";
    this.container.querySelectorAll("[data-choice]").forEach(function (chip) {
      chip.classList.remove("is-selected");
      chip.setAttribute("aria-pressed", "false");
    });
    this.hideError();
    if (this.defaultImg) {
      var scope = this.getScope();
      var imgs = scope.querySelectorAll(
        '[data-product-slide][data-id="' + this.doorMediaId + '"] img,' +
          '.product-single__thumbnail[data-id="' + this.doorMediaId + '"] img'
      );
      var d = this.defaultImg;
      imgs.forEach(function (img) {
        if (d.src) img.setAttribute("src", d.src);
        if (d.srcset) img.setAttribute("srcset", d.srcset);
      });
    }
  };

  PrehungKit.prototype.showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove("is-hidden");
  };

  PrehungKit.prototype.hideError = function () {
    if (!this.errorEl) return;
    this.errorEl.textContent = "";
    this.errorEl.classList.add("is-hidden");
  };

  PrehungKit.prototype.bindEvents = function () {
    var self = this;

    this.container.addEventListener("click", function (event) {
      var chip = event.target.closest("[data-choice]");
      if (!chip || !self.container.contains(chip)) return;
      self.setFinish(chip.value, { jump: true });
    });

    // Gate add to cart until size, swing and hinge finish are real choices.
    // custom.js already shows "Please select a door size!" when nothing has a
    // variant; this adds the swing message and the hinge message. Capture phase
    // so we can stop the theme's own submit before it runs.
    if (this.form) {
      // The newest instance owns the gate; bind the listener only once so re-inits
      // after a variant re-render do not stack duplicate handlers on the form.
      var form = this.form;
      form.__prehungActive = this;
      if (!form.__prehungGateBound) {
        form.__prehungGateBound = true;
        form.addEventListener(
          "submit",
          function (event) {
            var inst = form.__prehungActive;
            if (!inst) return;
            var message = "";
            var hasSize = Boolean(inst.getChecked(SIZE_OPTION));
            var hasSwing = Boolean(inst.getChecked(SWING_OPTION));
            if (hasSize && !hasSwing) {
              message = "Please choose a door swing: Left-hand or Right-hand.";
            } else if (hasSize && hasSwing && !inst.finish) {
              message = "Please choose a hinge colour.";
            }
            if (!message) return; // no size: custom.js shows its own size message
            event.preventDefault();
            event.stopImmediatePropagation();
            inst.showError(message);
            if (inst.container.scrollIntoView) {
              inst.container.scrollIntoView({ behavior: "smooth", block: "center" });
            }
          },
          true
        );
      }
    }

    // After a successful add, clear every option and scroll to the top (the
    // scroll-to-top itself is handled by custom.js's door-builder reset, which
    // this template opts into; here we just clear the hinge + image).

    // Size and swing are normal variant options, so listen where the theme
    // changes them. A change event on an option radio can only come from the
    // customer (the theme's re-render never fires one), so that is what we
    // remember as "chosen".
    var scope = this.getScope();
    if (scope) {
      scope.addEventListener("change", function (event) {
        var target = event.target;
        if (!target || !target.matches) return;
        if (target.matches("input[type='radio'][data-single-option-selector]")) {
          var name = optionName(target);
          if (name && target.checked) {
            self.chosen()[name] = target.value;
            self.hideError();
          }
          var isSwing = name.toLowerCase() === SWING_OPTION;
          setTimeout(function () {
            self.syncPhoto({ jump: isSwing });
          }, 0);
          return;
        }
        if (target.matches("[data-single-option-selector], select, input[name='id']")) {
          setTimeout(function () {
            self.syncPhoto();
          }, 0);
        }
      });
    }

    document.addEventListener("theme:product-info:loaded", function () {
      setTimeout(function () {
        self.afterThemeRender();
      }, 0);
    });
  };

  PrehungKit.prototype.setFinish = function (value, opts) {
    if (!value || value === this.finish) return;
    this.finish = value;
    finishMemory[memoryKey(this.container)] = value;

    if (this.finishInput) this.finishInput.value = value;
    this.hideError();

    this.container.querySelectorAll("[data-choice]").forEach(function (chip) {
      var selected = chip.value === value;
      chip.classList.toggle("is-selected", selected);
      chip.setAttribute("aria-pressed", selected ? "true" : "false");
    });

    this.syncPhoto(opts);
  };

  // Every option radio the theme currently shows (old, hidden copies excluded).
  PrehungKit.prototype.getOptionInputs = function () {
    var scope = this.getScope();
    if (!scope) return [];
    return Array.prototype.filter.call(
      scope.querySelectorAll("input[type='radio'][data-single-option-selector]"),
      isLiveInput
    );
  };

  // The checked value of one option group ("door size" / "door swing"), from the
  // radios the theme currently shows.
  PrehungKit.prototype.getChecked = function (optionKey) {
    var inputs = this.getOptionInputs();
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      if (optionName(input).toLowerCase() !== optionKey) continue;
      if (input.checked) return input.value;
    }
    return "";
  };

  // The swing the customer chose (never one the theme picked for them).
  PrehungKit.prototype.getSwing = function () {
    return this.chosenValue(SWING_OPTION);
  };

  // The theme hands every fetched fragment to product-info's preProcessHtmlCallbacks
  // BEFORE serialising and inserting it. Strip the auto-checked options there, so
  // the inserted markup never has a swing (or size) the customer did not click and
  // nothing can flash on for a frame. enforceChosen() below stays as the backstop.
  PrehungKit.prototype.installPreRender = function () {
    var info = this.productInfo;
    if (!info || !Array.isArray(info.preProcessHtmlCallbacks) || info.__prehungPreRender) return;
    info.__prehungPreRender = true;
    var self = this;
    info.preProcessHtmlCallbacks.push(function (fragment) {
      if (!fragment || typeof fragment.querySelectorAll !== "function") return;
      var chosen = self.chosen();
      fragment
        .querySelectorAll("input[type='radio'][data-single-option-selector][checked]")
        .forEach(function (input) {
          var name = optionName(input);
          if (!name || chosen[name] === input.value) return;
          input.removeAttribute("checked");
          input.checked = false;
        });
    });
  };

  // After the theme re-renders the options from a resolved variant: uncheck every
  // option the customer did not click, and clear the variant id so add to cart
  // stays gated. Idempotent; runs on every DOM change under product-info.
  PrehungKit.prototype.enforceChosen = function () {
    var chosen = this.chosen();
    var cleared = false;
    this.getOptionInputs().forEach(function (input) {
      var name = optionName(input);
      if (!name || !input.checked) return;
      if (chosen[name] === input.value) return;
      input.checked = false;
      input.removeAttribute("checked");
      cleared = true;
    });
    if (cleared) this.clearVariantId();
  };

  PrehungKit.prototype.clearVariantId = function () {
    if (!this.form) return;
    var input = this.form.querySelector("input[name='id']");
    if (input && input.value) {
      input.value = "";
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }
    // The ordered-in callout and stock badge describe the resolved size; with no
    // size chosen they must not show (same rule as custom.js on load).
    if (!this.chosenValue(SIZE_OPTION)) {
      this.getScope()
        .querySelectorAll("[data-exos-ordered-in], .exos-countdown--ordered-in")
        .forEach(function (el) {
          el.setAttribute("hidden", "");
        });
    }
  };

  PrehungKit.prototype.afterThemeRender = function () {
    this.enforceChosen();
    this.syncPhoto();
  };

  PrehungKit.prototype.getConfigUrl = function () {
    var swing = this.getSwing();
    if (!swing) return null;
    return this.configMap[swing + "|" + (this.finish || DEFAULT_FINISH)] || null;
  };

  // Bring the gallery back to the door render so a photo change is actually seen.
  PrehungKit.prototype.showDoorSlide = function () {
    var scope = this.getScope();
    var slider = scope.querySelector("[data-product-single-media-slider]");
    var Flickity = window.theme && window.theme.Flickity;
    if (!slider || !Flickity || typeof Flickity.data !== "function") return;
    var flkty = Flickity.data(slider);
    if (!flkty || !flkty.isActive || !flkty.cells) return;
    var selector = '[data-id="' + this.doorMediaId + '"]';
    for (var i = 0; i < flkty.cells.length; i++) {
      var el = flkty.cells[i].element;
      if (el && (el.matches(selector) || el.querySelector(selector))) {
        if (flkty.selectedIndex !== i) flkty.select(i);
        return;
      }
    }
  };

  // Swap the one door image (main slide + its thumbnail) to the render for the
  // current configuration. Only the <img> source changes; the gallery cells
  // stay put, so Flickity and the lightbox are untouched.
  PrehungKit.prototype.syncPhoto = function (opts) {
    var url = this.getConfigUrl();
    if (!url) return;
    if (!this.doorMediaId) return;

    var scope = this.getScope();
    var imgs = scope.querySelectorAll(
      '[data-product-slide][data-id="' + this.doorMediaId + '"] img,' +
        '.product-single__thumbnail[data-id="' + this.doorMediaId + '"] img'
    );

    imgs.forEach(function (img) {
      // srcset would override src on responsive images, so point every
      // candidate at the same render (the theme requests a width via ?width=,
      // which this CDN url honours).
      if (img.hasAttribute("srcset")) {
        var widths = [];
        img.getAttribute("srcset").split(",").forEach(function (part) {
          var m = part.trim().match(/(\d+)w\s*$/);
          if (m) widths.push(m[1]);
        });
        if (widths.length) {
          var joined = "&";
          if (url.indexOf("?") === -1) joined = "?";
          var newset = widths
            .map(function (w) {
              return url + joined + "width=" + w + " " + w + "w";
            })
            .join(", ");
          img.setAttribute("srcset", newset);
        }
      }
      img.setAttribute("src", url);
      // clear any lazy-load state so the new image paints
      img.classList.remove("lazyload", "is-loading");
      img.classList.add("lazyloaded");
      var fig = img.closest("figure");
      if (fig) fig.classList.remove("is-loading");
    });

    if (opts && opts.jump) this.showDoorSlide();
  };

  function init(scope) {
    (scope || document).querySelectorAll("[data-prehung-kit]").forEach(function (container) {
      if (container.__prehungKit) return;
      container.__prehungKit = new PrehungKit(container);
    });
    watch();
  }

  function resetAll() {
    document.querySelectorAll("[data-prehung-kit]").forEach(function (container) {
      if (container.__prehungKit) container.__prehungKit.reset();
    });
  }

  // The theme rebuilds the variant markup without firing a theme event, so watch
  // for it: re-initialise whatever came back and undo any option it checked on
  // the customer's behalf.
  function watch() {
    var host = document.querySelector("product-info");
    if (!host || host.__prehungWatch) return;
    host.__prehungWatch = true;
    var pending = false;
    new MutationObserver(function () {
      // Right away (a microtask, so still before the next paint): undo anything the
      // theme checked on the customer's behalf and clear the variant id it filled.
      host.querySelectorAll("[data-prehung-kit]").forEach(function (container) {
        if (container.__prehungKit) container.__prehungKit.enforceChosen();
      });
      if (pending) return;
      pending = true;
      setTimeout(function () {
        pending = false;
        host.querySelectorAll("[data-prehung-kit]").forEach(function (container) {
          if (!container.__prehungKit) {
            container.__prehungKit = new PrehungKit(container);
            return;
          }
          container.__prehungKit.afterThemeRender();
        });
      }, 0);
    }).observe(host, { childList: true, subtree: true });
  }

  document.addEventListener("exos:cart:added", function () {
    setTimeout(resetAll, 0);
  });
  document.addEventListener("exos:door-configurator:reset", resetAll);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("theme:product-info:loaded", function (event) {
    init(event.target || document);
  });
  document.addEventListener("shopify:section:load", function (event) {
    init(event.target || document);
  });
})();
